/**
 * Review API - Document review workflow management.
 *
 * Provides operations for:
 * - Review queue CRUD
 * - Document assignment and routing
 * - Review decisions with audit trail
 * - Reviewer statistics
 */

import apiClient, { type RequestConfig } from './client';
import type {
  AssignmentListParams,
  BulkAssignRequest,
  DecisionCreateRequest,
  EscalateRequest,
  MatterReviewSummary,
  QueueCreateRequest,
  QueueListParams,
  QueueStatistics,
  QueueUpdateRequest,
  ReviewAssignment,
  ReviewDecision,
  ReviewerStatistics,
  ReviewerStatsParams,
  ReviewQueue,
  SkipRequest,
} from './review-types';

const ENDPOINT = '/review';

export const reviewApi = {
  // ---------------------------------------------------------------------------
  // Queue Operations
  // ---------------------------------------------------------------------------

  /**
   * Create a new review queue.
   */
  async createQueue(
    request: QueueCreateRequest,
    config?: RequestConfig
  ): Promise<ReviewQueue> {
    const { data } = await apiClient.post<ReviewQueue>(
      `${ENDPOINT}/queues`,
      request,
      config
    );
    return data;
  },

  /**
   * List review queues for a matter.
   */
  async listQueues(
    params: QueueListParams,
    config?: RequestConfig
  ): Promise<ReviewQueue[]> {
    const { data } = await apiClient.get<ReviewQueue[]>(`${ENDPOINT}/queues`, {
      ...config,
      params: params as unknown as Record<string, string | number | boolean | undefined>,
    });
    return data;
  },

  /**
   * Get a review queue by ID.
   */
  async getQueue(
    queueId: string,
    config?: RequestConfig
  ): Promise<ReviewQueue> {
    const { data } = await apiClient.get<ReviewQueue>(
      `${ENDPOINT}/queues/${queueId}`,
      config
    );
    return data;
  },

  /**
   * Update a review queue.
   */
  async updateQueue(
    queueId: string,
    request: QueueUpdateRequest,
    config?: RequestConfig
  ): Promise<ReviewQueue> {
    const { data } = await apiClient.patch<ReviewQueue>(
      `${ENDPOINT}/queues/${queueId}`,
      request,
      config
    );
    return data;
  },

  /**
   * Deactivate (soft delete) a review queue.
   */
  async deactivateQueue(
    queueId: string,
    config?: RequestConfig
  ): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/queues/${queueId}`, config);
  },

  /**
   * Get statistics for a review queue.
   */
  async getQueueStats(
    queueId: string,
    config?: RequestConfig
  ): Promise<QueueStatistics> {
    const { data } = await apiClient.get<QueueStatistics>(
      `${ENDPOINT}/queues/${queueId}/stats`,
      config
    );
    return data;
  },

  /**
   * Bulk assign documents to a queue.
   */
  async bulkAssign(
    queueId: string,
    request: BulkAssignRequest,
    config?: RequestConfig
  ): Promise<ReviewAssignment[]> {
    const { data } = await apiClient.post<ReviewAssignment[]>(
      `${ENDPOINT}/queues/${queueId}/assign`,
      request,
      config
    );
    return data;
  },

  /**
   * Get the next document to review from a queue.
   * Automatically assigns the document to the requesting reviewer.
   */
  async getNextDocument(
    queueId: string,
    config?: RequestConfig
  ): Promise<ReviewAssignment | null> {
    const { data } = await apiClient.get<ReviewAssignment | null>(
      `${ENDPOINT}/queues/${queueId}/next`,
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Assignment Operations
  // ---------------------------------------------------------------------------

  /**
   * List review assignments with filters.
   */
  async listAssignments(
    params: AssignmentListParams,
    config?: RequestConfig
  ): Promise<ReviewAssignment[]> {
    const { data } = await apiClient.get<ReviewAssignment[]>(
      `${ENDPOINT}/assignments`,
      {
        ...config,
        params: params as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Get an assignment by ID.
   */
  async getAssignment(
    assignmentId: string,
    config?: RequestConfig
  ): Promise<ReviewAssignment> {
    const { data } = await apiClient.get<ReviewAssignment>(
      `${ENDPOINT}/assignments/${assignmentId}`,
      config
    );
    return data;
  },

  /**
   * Record a review decision.
   */
  async recordDecision(
    assignmentId: string,
    request: DecisionCreateRequest,
    config?: RequestConfig
  ): Promise<ReviewDecision> {
    const { data } = await apiClient.post<ReviewDecision>(
      `${ENDPOINT}/assignments/${assignmentId}/decide`,
      request,
      config
    );
    return data;
  },

  /**
   * Skip a document (returns to queue).
   */
  async skipDocument(
    assignmentId: string,
    request?: SkipRequest,
    config?: RequestConfig
  ): Promise<ReviewAssignment> {
    const { data } = await apiClient.post<ReviewAssignment>(
      `${ENDPOINT}/assignments/${assignmentId}/skip`,
      request || {},
      config
    );
    return data;
  },

  /**
   * Escalate a document to another reviewer.
   */
  async escalateDocument(
    assignmentId: string,
    request: EscalateRequest,
    config?: RequestConfig
  ): Promise<ReviewAssignment> {
    const { data } = await apiClient.post<ReviewAssignment>(
      `${ENDPOINT}/assignments/${assignmentId}/escalate`,
      request,
      config
    );
    return data;
  },

  /**
   * Get decision history for an assignment.
   */
  async getDecisionHistory(
    assignmentId: string,
    config?: RequestConfig
  ): Promise<ReviewDecision[]> {
    const { data } = await apiClient.get<ReviewDecision[]>(
      `${ENDPOINT}/assignments/${assignmentId}/history`,
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Matter Summary
  // ---------------------------------------------------------------------------

  /**
   * Get review summary for a matter.
   */
  async getMatterSummary(
    matterId: string,
    config?: RequestConfig
  ): Promise<MatterReviewSummary> {
    const { data } = await apiClient.get<MatterReviewSummary>(
      `${ENDPOINT}/matters/${matterId}/summary`,
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Reviewer Statistics
  // ---------------------------------------------------------------------------

  /**
   * Get statistics for a reviewer.
   */
  async getReviewerStats(
    reviewerId: string,
    params: ReviewerStatsParams,
    config?: RequestConfig
  ): Promise<ReviewerStatistics> {
    const { data } = await apiClient.get<ReviewerStatistics>(
      `${ENDPOINT}/reviewers/${reviewerId}/stats`,
      {
        ...config,
        params: params as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Get current user's review statistics.
   */
  async getMyStats(
    params: ReviewerStatsParams,
    config?: RequestConfig
  ): Promise<ReviewerStatistics> {
    const { data } = await apiClient.get<ReviewerStatistics>(
      `${ENDPOINT}/reviewers/me/stats`,
      {
        ...config,
        params: params as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },
};

export default reviewApi;
